/**
 * Account login — TCP-native password authentication with two email options.
 *
 * UI presents three explicit actions:
 *   1. Email + password sign-in           → POST /api/auth/login
 *   2. "Email me a magic login link"      → POST /api/auth/magic-link/request
 *                                            (lands on ?next path or /dashboard)
 *   3. "Forgot password? Reset it via email" → POST /api/auth/magic-link/request
 *                                            (lands on /account/reset-password?from=magic)
 *
 * Backend contract is unchanged — both magic-link buttons hit the same
 * endpoint; only the `next` parameter differs.  TCP password auth is fully
 * independent of any Shopify customer-account password.
 */
import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Mail, Lock, ArrowLeft, ArrowRight, KeyRound } from "lucide-react";
import api from "../lib/api";
import { track } from "../lib/analytics";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

function Frame({ children }) {
    return (
        <div className="min-h-screen bg-[#0B1220] text-[#CBD5E1] flex flex-col">
            <div className="h-px bg-gradient-to-r from-transparent via-[#56CCF2]/70 to-transparent" />
            <main className="flex-1 flex items-start justify-center px-6 pt-20 pb-16">
                <div className="w-full max-w-md">{children}</div>
            </main>
            <footer className="text-center pb-10 px-6 text-[10px] uppercase tracking-[0.22em] text-[#64748B]">
                <Link to="/" className="hover:text-[#56CCF2] transition-colors inline-flex items-center gap-1.5" data-testid="account-login-back-home">
                    <ArrowLeft className="w-3 h-3" />
                    Back to the library
                </Link>
            </footer>
        </div>
    );
}

function Eyebrow() {
    return (
        <div className="mb-6">
            <div className="flex items-center gap-3 mb-1">
                <div className="h-9 px-2 flex items-center justify-center rounded-md bg-white"><img src={require("../assets/logo-tcp-mark.png")} alt="The Context Project" className="h-6 w-auto select-none" draggable={false} /></div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-semibold">The Context Project</div>
                    <div className="text-[11px] text-[#64748B]">Members archive · sign in</div>
                </div>
            </div>
        </div>
    );
}

const RESET_NEXT = "/account/reset-password?from=magic";

export default function AccountLoginPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { hydrateFromCustomerLogin } = useAuth();
    const nextPath = params.get("next") || "/dashboard";
    // Pre-fill email from URL — used after Shopify checkout when the user
    // already has a TCP account with a password.
    const [email, setEmail] = useState(params.get("email") || "");
    const [password, setPassword] = useState("");
    const [rememberMe, setRememberMe] = useState(true);
    const [busy, setBusy] = useState(false);
    // Intent that was just sent — "login" or "reset" — drives the
    // confirmation screen copy. null while the password form is shown.
    const [sentIntent, setSentIntent] = useState(null);
    const error = params.get("error");

    useEffect(() => { track("tcp_login_view", { mode: "password" }); }, []);

    const submitPassword = async (e) => {
        e.preventDefault();
        if (!email.trim() || !password) return;
        setBusy(true);
        try {
            // TCP-native password auth. Authenticates against the password
            // the user set during /activate-account → /auth/claim. Completely
            // independent of any Shopify customer-account password.
            const { data } = await api.post("/auth/login", {
                email: email.trim().toLowerCase(),
                password,
                remember_me: rememberMe,
            });
            track("tcp_login_success", { mode: "password", remember_me: rememberMe });
            // Pass the JWT to hydrate → AuthContext persists it to localStorage
            // as the Bearer fallback for iOS Safari ITP edge cases.
            if (data?.user) hydrateFromCustomerLogin(data.user, data.token);
            navigate(nextPath);
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail || "Sign-in failed.";
            if (status === 401) {
                toast.error("Invalid email or password.");
            } else if (status === 429) {
                toast.error("Too many sign-in attempts. Please wait a few minutes.");
            } else {
                toast.error(typeof detail === "string" ? detail : "Sign-in failed.");
            }
        } finally {
            setBusy(false);
        }
    };

    // Single helper for both "magic login" and "reset password" buttons.
    // The only difference is the `next` parameter we hand to the backend,
    // which drives where the email link lands the user after verify.
    const sendMagic = async (intent) => {
        if (!email.trim()) {
            toast.error("Enter your email first.");
            return;
        }
        const destination = intent === "reset" ? RESET_NEXT : nextPath;
        setBusy(true);
        try {
            await api.post("/auth/magic-link/request", {
                email: email.trim().toLowerCase(),
                next: destination,
            });
            track(intent === "reset" ? "tcp_password_reset_requested" : "tcp_login_magic_requested");
            setSentIntent(intent);
        } catch {
            toast.error("Something went wrong. Please try again in a moment.");
        } finally {
            setBusy(false);
        }
    };

    if (sentIntent) {
        const isReset = sentIntent === "reset";
        return (
            <Frame>
                <Eyebrow />
                <h1 className="text-3xl font-bold text-white tracking-tight mb-3" data-testid="account-login-sent-heading">
                    {isReset ? "Password reset link sent." : "Your sign-in link is on its way."}
                </h1>
                <p className="text-[15px] text-[#94A3B8] leading-relaxed mb-6">
                    {isReset ? (
                        <>
                            We've emailed <span className="text-white">{email}</span> a secure link. Open it to
                            choose a new password. The link is valid for 30 minutes.
                        </>
                    ) : (
                        <>
                            We've emailed <span className="text-white">{email}</span> a secure sign-in link.
                            It's valid for 30 minutes. If you don't see it, check your spam folder.
                        </>
                    )}
                </p>
                <button
                    type="button"
                    data-testid="account-login-sent-back"
                    onClick={() => { setSentIntent(null); }}
                    className="text-xs uppercase tracking-[0.22em] text-[#56CCF2] hover:text-white inline-flex items-center gap-2"
                >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    Use a different email
                </button>
            </Frame>
        );
    }

    return (
        <Frame>
            <Eyebrow />
            <h1 className="text-3xl font-bold text-white tracking-tight mb-3">Welcome back.</h1>
            <p className="text-[15px] text-[#94A3B8] leading-relaxed mb-8">
                Sign in with your member email and password to continue the investigation.
            </p>
            {error && (
                <div className="mb-5 px-4 py-3 rounded-lg bg-[#FB7185]/10 border border-[#FB7185]/30 text-sm text-[#FB7185]">
                    {error === "expired" ? "That link has expired — request a fresh one." : "Sign-in failed. Please try again."}
                </div>
            )}

            <form onSubmit={submitPassword} className="space-y-4" data-testid="account-login-form">
                <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.22em] text-[#64748B] mb-2 block">Email</span>
                    <div className="flex items-center gap-3 bg-[#111827] border border-[#243044] rounded-xl px-4 py-3 focus-within:border-[#2F80ED]">
                        <Mail className="w-4 h-4 text-[#64748B]" />
                        <input
                            data-testid="account-login-email"
                            type="email"
                            required
                            autoFocus
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@email.com"
                            className="bg-transparent border-0 outline-none text-white placeholder-[#64748B] flex-1 text-sm"
                        />
                    </div>
                </label>

                <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.22em] text-[#64748B] mb-2 block">Password</span>
                    <div className="flex items-center gap-3 bg-[#111827] border border-[#243044] rounded-xl px-4 py-3 focus-within:border-[#2F80ED]">
                        <Lock className="w-4 h-4 text-[#64748B]" />
                        <input
                            data-testid="account-login-password"
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            className="bg-transparent border-0 outline-none text-white placeholder-[#64748B] flex-1 text-sm"
                        />
                    </div>
                </label>

                <label className="flex items-center gap-2 select-none cursor-pointer" data-testid="account-login-remember-row">
                    <input
                        type="checkbox"
                        data-testid="account-login-remember"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="w-4 h-4 rounded border-[#243044] bg-[#111827] text-[#2F80ED] focus:ring-[#2F80ED] focus:ring-offset-0 cursor-pointer"
                    />
                    <span className="text-xs text-[#94A3B8]">Keep me signed in for 30 days</span>
                </label>

                <button
                    data-testid="account-login-submit"
                    type="submit"
                    disabled={busy}
                    className="w-full mt-2 px-5 py-3.5 rounded-xl bg-[#2F80ED] hover:bg-[#1E6FE0] disabled:opacity-60 text-white text-sm font-semibold tracking-wide transition-colors inline-flex items-center justify-center gap-2"
                >
                    {busy ? "Working…" : "Sign in"}
                    <ArrowRight className="w-4 h-4" />
                </button>
            </form>

            {/* Divider */}
            <div className="my-6 flex items-center gap-3" aria-hidden="true">
                <div className="h-px flex-1 bg-[#243044]" />
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#64748B]">or</span>
                <div className="h-px flex-1 bg-[#243044]" />
            </div>

            {/* Two distinct email-based options */}
            <div className="space-y-3">
                <button
                    type="button"
                    data-testid="account-login-magic-link"
                    onClick={() => sendMagic("login")}
                    disabled={busy}
                    className="w-full px-5 py-3 rounded-xl bg-[#111827] border border-[#243044] hover:border-[#56CCF2]/60 text-[#CBD5E1] hover:text-white text-sm font-medium tracking-wide transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                    <Mail className="w-4 h-4 text-[#56CCF2]" />
                    Email me a magic login link
                </button>
                <button
                    type="button"
                    data-testid="account-login-reset-password"
                    onClick={() => sendMagic("reset")}
                    disabled={busy}
                    className="w-full px-5 py-3 rounded-xl bg-[#111827] border border-[#243044] hover:border-[#56CCF2]/60 text-[#CBD5E1] hover:text-white text-sm font-medium tracking-wide transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                    <KeyRound className="w-4 h-4 text-[#56CCF2]" />
                    Forgot password? Reset it via email
                </button>
            </div>

            <div className="mt-8 text-center text-xs text-[#64748B]">
                <p>
                    Not a member yet?{" "}
                    <Link to="/account/signup" data-testid="account-login-signup-link" className="text-[#56CCF2] hover:text-white">
                        Join the archive
                    </Link>
                </p>
            </div>
        </Frame>
    );
}
